import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

const sel = "min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm";

function WhyPanel({ lib, lookups, recommended }) {
  const ex = lib.explanation;
  const h = ex.previous_dispute_status || {};
  return (
    <section className="rounded-2xl border border-purple-200 bg-purple-50/60 p-4 text-sm" data-testid="letter-why">
      <h3 className="font-semibold text-slate-900">{recommended ? "Why this letter was recommended" : "Why this letter is eligible"}</h3>
      <ul className="mt-2 space-y-1 text-slate-700">
        <li>Current round: {ex.current_round === "special" ? "Special request" : `Round ${ex.current_round}`}</li>
        <li>Recipient type: {ex.recipient_type}</li>
        <li>Previous disputes on this account: {h.sent || 0} mailed, {h.responded || 0} with a recorded response</li>
        <li>Evidence available: {ex.evidence.length ? ex.evidence.join(", ") : "None listed"}</li>
        {ex.rules_satisfied.map((r) => <li key={r}>✓ {r}</li>)}
        <li>Writing structure: {lookups.structures[lib.structure]} · Template v{lib.template_version}{lib.ai_used ? "" : " · Approved standard wording used"}</li>
      </ul>
    </section>
  );
}

function SourceFacts({ facts, lookups }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 text-sm" data-testid="letter-source-facts">
      <h3 className="font-semibold text-slate-900">Source facts (never changed by regeneration)</h3>
      <ul className="mt-2 space-y-1 text-slate-700">
        {facts.disputed_fields.map((f) => <li key={f.field}>{lookups.fields[f.field]}: report shows “{f.reported_value || "not shown"}”, should show “{f.correct_value}”</li>)}
        <li>Requested: {lookups.actions[facts.requested_action]}</li>
        {facts.prior_dispute && <li>Previous dispute: {facts.prior_dispute.date} · {lookups.prior_results[facts.prior_dispute.result]}</li>}
      </ul>
    </section>
  );
}

export function GeneratedLetter({ doc, setDoc, lookups, structures, recommended, onBack }) {
  const lib = doc.library;
  const [structure, setStructure] = useState(lib.structure);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const regen = async () => {
    setBusy(true);
    try { setDoc((await api.post(`/letter-library/documents/${doc.id}/regenerate`, { structure })).data); toast.success("Regenerated with the same facts"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not regenerate"); } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    try { setDoc((await api.put(`/letter-library/documents/${doc.id}/narrative`, edit)).data); setEdit(null); toast.success("Saved. Please sign again before mailing."); }
    catch (e) { toast.error((e?.response?.data?.detail?.issues || []).join(" ") || "Edit rejected"); }
  };
  return (
    <div className="space-y-3" data-testid="generated-letter">
      <WhyPanel lib={lib} lookups={lookups} recommended={recommended} />
      <SourceFacts facts={lib.facts} lookups={lookups} />
      <pre className="whitespace-pre-wrap break-words rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-900 font-sans max-h-[60vh] overflow-auto" data-testid="letter-preview">{doc.content}</pre>
      <div className="flex flex-col sm:flex-row gap-2">
        <select className={sel} value={structure} onChange={(e) => setStructure(e.target.value)} data-testid="letter-structure-select">
          {structures.map((s) => <option key={s} value={s}>{lookups.structures[s]}</option>)}
        </select>
        <Button variant="outline" onClick={regen} disabled={busy} className="min-h-[44px] rounded-xl" data-testid="letter-regenerate">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <><RefreshCw size={15} className="mr-2" />Regenerate in this style</>}
        </Button>
        <Button variant="outline" onClick={() => setEdit({ ...lib.narrative })} className="min-h-[44px] rounded-xl" data-testid="letter-edit">Edit wording</Button>
      </div>
      {edit && (
        <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="letter-edit-form">
          <p className="text-xs text-slate-500">You can edit the narrative paragraphs. Account facts, citations and required sections stay fixed.</p>
          <Textarea rows={3} value={edit.summary} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} data-testid="letter-edit-summary" />
          <Textarea rows={5} value={edit.explanation} onChange={(e) => setEdit({ ...edit, explanation: e.target.value })} data-testid="letter-edit-explanation" />
          <Button onClick={saveEdit} className="min-h-[44px] rounded-xl btn-primary" data-testid="letter-edit-save">Save wording</Button>
        </div>
      )}
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="letter-saved-draft">
        Saved as a draft. To sign, download, or mail it, open it on the Documents page.
        <Link to="/app/documents" className="mt-3 flex items-center justify-center min-h-[44px] rounded-xl bg-emerald-600 text-white font-semibold" data-testid="letter-open-documents">Sign, Download or Mail</Link>
      </div>
      <Button variant="outline" onClick={onBack} className="w-full sm:w-auto min-h-[44px] rounded-xl" data-testid="letter-back-to-library">Start another letter</Button>
    </div>
  );
}
